import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ApiService, ViewTypeEnum, Request, FormTypeEnum } from "../../api";
import { Alert, Button, Collapse, Space, Spin, Steps } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { StepProps } from "antd/lib";
import { CreateStepType, openNotificationWithIcon } from "../../constants";
import { useCloseOwnTab } from "../../tabs/tabContext";
import WorkflowViewPage from "./WorkflowViewPage";
import RenderFormComponent from "../../components/RenderFormComponent";
import RequestHeader from "../../components/RequestHeader";
import { CaretRightOutlined } from "@ant-design/icons";

export type CreateStepsProps = {
  form_id?: number;
  schema?: any;
  form?: any;
  workflow_id?: number;
  type: CreateStepType;
  formType?: FormTypeEnum;
  // Whether the current user may fill this section right now (permission AND the
  // section's stage is active). false -> render read-only.
  editable_now?: boolean;
  fill_group_name?: string | null;
  // Which gate is holding it: 'not_owner' | 'stage_not_open' | 'not_applicable'
  // | null.
  editable_reason?: string | null;
  // The stage this section belongs to, for a 'stage_not_open' hint.
  stage_name?: string | null;
  // False when this section's own rule rules it out for this case. Distinct
  // from the other two gates: it is not waiting for anything.
  applies_now?: boolean;
  // The rule in words, so the hint can say why rather than only that.
  condition_text?: string;
} & StepProps;

/** Banner shown above a section the current user is not allowed to fill. */
/**
 * Why a section is read-only, in the words that fit the reason.
 *
 * `editable_now` alone could not be worded honestly: it folds "not yours" and
 * "not yet" into one boolean, so a section held by the process was explained as
 * one held by another person. The backend now says which (`editable_reason`)
 * and, for a timing lock, which stage it waits for.
 */
export const ReadOnlySectionHint = ({
  fillGroupName,
  reason,
  stageName,
  conditionText,
}: {
  fillGroupName?: string | null;
  reason?: string | null;
  stageName?: string | null;
  conditionText?: string;
}) => {
  const { t } = useTranslation();

  const message = () => {
    // Checked first, and worded as a fact rather than a wait: a section its
    // own rule has ruled out is not going to open later, whatever the stage
    // does, so "opens at the X stage" would send somebody to wait for nothing.
    if (reason === "not_applicable") {
      return conditionText
        ? t("Not needed on this case — it is only asked for when {{rule}}.", {
            rule: conditionText,
          })
        : t("Not needed on this case: its rule does not match these answers.");
    }
    if (reason === "stage_not_open") {
      return stageName
        ? t("This section opens at the “{{stage}}” stage. It is read-only for now.", {
            stage: stageName,
          })
        : t("This section is not open at the current stage of this process.");
    }
    return fillGroupName
      ? t("This section is filled by {{group}}. It is read-only for you.", {
          group: fillGroupName,
        })
      : t("This section is filled by someone else. It is read-only for you.");
  };

  return (
    <Alert
      type="info"
      showIcon
      icon={<LockOutlined />}
      style={{ marginBottom: 12 }}
      message={message()}
    />
  );
};

const EditRequestPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const location = useNavigate();
  const closeOwnTab = useCloseOwnTab();
  const [current, setCurrent] = useState<number>(0);
  const [viewType, setViewType] = useState<ViewTypeEnum>(ViewTypeEnum.COLLAPSE);
  const [items, setItems] = useState<CreateStepsProps[]>([]);
  const [request, setRequest] = useState<Request | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  // Set while the last section is being saved and its document regenerated —
  // that round trip is slow enough that the page looked frozen without it.
  const [finishing, setFinishing] = useState<boolean>(false);

  // One fetch feeds the header, the view type and the sections; this used to be
  // two effects requesting the same permit twice on every mount.
  const loadRequest = async () => {
    setLoading(true);
    try {
      const result = await ApiService.apiMainRequestsRetrieve(parseInt(id!));
      setRequest(result);
      setViewType(result.template.view_type ?? ViewTypeEnum.COLLAPSE);

      const forms: CreateStepsProps[] = result.template.form_templates.map(
        (ft, index) => ({
          key: index,
          title: t(ft.title),
          form_id: ft.id,
          form: ft.schema_json.form,
          schema: ft.schema_json.schema,
          type: CreateStepType.Form,
          formType: ft.type,
          // Injected by the API per current user (see FormSerializer).
          editable_now: (ft as any).editable_now,
          fill_group_name: (ft as any).fill_group_name,
          editable_reason: (ft as any).editable_reason,
          stage_name: (ft as any).stage_name,
          applies_now: (ft as any).applies_now,
          condition_text: (ft as any).condition_text,
        })
      );

      const workflows: CreateStepsProps[] = result.template.workflows.map(
        (ft, index) => ({
          key: index,
          title: t(ft.name),
          workflow_id: ft.id,
          type: CreateStepType.Workflow,
        })
      );

      setItems([...forms, ...workflows]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Leaving for the permit itself: close this Edit tab first, so the user lands
  // on the request instead of stacking up a stale editor behind it. Closing
  // after navigating would race the workspace's open-tab effect.
  const backToRequest = () => {
    closeOwnTab();
    location("/requests/" + parseInt(id!));
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const handleSuccessSubmit = async (result: any) => {
    if (current === items.length - 1) {
      openNotificationWithIcon("success", t("Form successfully saved"));
      setFinishing(true);
      try {
        await ApiService.apiMainGenerateDocumentRetrieve(parseInt(id!));
      } finally {
        setFinishing(false);
      }
      backToRequest();
    } else {
      setCurrent(current + 1);
    }
  };

  const renderView = () => {
    if (items.length === 0) {
      return null;
    }

    if (items[current].type === CreateStepType.Form) {
      const editable = items[current].editable_now !== false;
      return (
        <>
          {!editable && (
            <ReadOnlySectionHint
              fillGroupName={items[current].fill_group_name}
              reason={items[current].editable_reason}
              stageName={items[current].stage_name}
              conditionText={items[current].condition_text}
            />
          )}
          <RenderFormComponent
            form={items[current]}
            requestId={parseInt(id!)}
            readonly={!editable}
            onSuccess={handleSuccessSubmit}
            showPrev={current > 0}
            onPrev={prev}
          />
        </>
      );
    }

    return (
      <WorkflowViewPage
        requestId={parseInt(id!)}
        workflowId={items[current].workflow_id}
        onSuccess={handleSuccessSubmit}
        showPrev={current > 0}
        onPrev={prev}
      />
    );
  };

  // Nothing to lay out until the sections arrive; showing an empty Steps bar or
  // an empty accordion first made the page look broken.
  if (loading && items.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "64px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (viewType === ViewTypeEnum.STEP) {
    return (
      <Spin spinning={finishing} tip={t("Saving")}>
        {request && (
          <RequestHeader
            request={request}
            actions={
              <Button type="primary" onClick={backToRequest}>
                {t("Return Back")}
              </Button>
            }
          />
        )}
        <Steps current={current} items={items} onChange={setCurrent} />
        <div style={{ marginTop: 24 }}>{renderView()}</div>
      </Spin>
    );
  }

  return (
    <Spin spinning={finishing} tip={t("Saving")}>
      {request && (
        <RequestHeader
          request={request}
          actions={
            <Button type="primary" onClick={backToRequest}>
              {t("Return Back")}
            </Button>
          }
        />
      )}

      <Space direction="vertical" style={{ width: "100%" }}>
        {items.map((page) => (
          <Collapse
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
            ghost={true}
            items={[
              {
                key: page.form_id,
                label: page.title,
                children:
                  page.type === CreateStepType.Form ? (
                    <>
                      {page.editable_now === false && (
                        <ReadOnlySectionHint
                          fillGroupName={page.fill_group_name}
                          reason={page.editable_reason}
                          stageName={page.stage_name}
                          conditionText={page.condition_text}
                        />
                      )}
                      <RenderFormComponent
                        form={page}
                        requestId={parseInt(id!)}
                        readonly={page.editable_now === false}
                        onSuccess={handleSuccessSubmit}
                      />
                    </>
                  ) : (
                    <WorkflowViewPage
                      requestId={parseInt(id!)}
                      workflowId={page.workflow_id}
                      onSuccess={handleSuccessSubmit}
                    />
                  ),
              },
            ]}
          />
        ))}
      </Space>
    </Spin>
  );
};

export default EditRequestPage;
